/**
 * Wallet — Subscription & Delivery Management Hub
 *
 * THE central page for agents to manage everything about their subscription:
 * - Subscription status (active, paused, cancelling, cancelled)
 * - Contract delivery progress (aggregated across all contracts)
 * - Billing management (payment method, invoices via Stripe portal)
 * - Payment activity history
 * - Contract breakdown (multi-contract agents)
 * - Delivery history (recent leads)
 *
 * PRINCIPLES:
 * - Single source of truth for the agent's financial & delivery relationship
 * - Clear, trust-building presentation of subscription state
 * - No internal system terms exposed to the client
 * - Graceful handling of all contract statuses (active, paused, cancelled, etc.)
 * - Brand-consistent UI: navy/gold/cream palette, clean typography
 *
 * CONFLICT PREVENTION:
 * The subscription state displayed here is derived from the contract status
 * which is set by the Stripe webhook handlers:
 *   - invoice.paid → renews cycle (active)
 *   - invoice.payment_failed → pauses contract (paused)
 *   - customer.subscription.updated → sets cancel_at_period_end flag (cancelling)
 *   - customer.subscription.deleted → marks cancelled (cancelled)
 * Each handler has a clearly defined responsibility with no overlap.
 * See stripe-webhook/index.ts for the full conflict prevention documentation.
 */

import {
  FileText,
  TrendingUp,
  ArrowRight,
  Loader2,
  UserCheck,
  Wallet as WalletIcon,
  PauseCircle,
  PlayCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import DashboardLayout from "@/components/dashboard/DashboardLayout";
import { ContractsBreakdownCard, type ContractBreakdownItem } from "@/components/dashboard/ContractsBreakdownCard";
import { SubscriptionStatusBanner, type SubscriptionState } from "@/components/dashboard/SubscriptionStatusBanner";
import { BillingManagementCard } from "@/components/dashboard/BillingManagementCard";
import { PaymentActivityFeed } from "@/components/dashboard/PaymentActivityFeed";
import { AgentAmendmentHistory } from "@/components/dashboard/AgentAmendmentHistory";
import { useAgent } from "@/contexts/AgentContext";
import { useContracts } from "@/contexts/ContractsContext";
import { useEnsureContractClaimed } from "@/hooks/useEnsureContractClaimed";
import { getCycleDays, getMaxPauseDays, getPlanLabel } from "@/lib/planDisplay";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import { Link } from "react-router-dom";
import { useMemo, useState, useEffect, useCallback } from "react";

interface DeliveryEvent {
  id: string;
  lead_type_name: string;
  created_at: number;
  last_updated_at: number;
  lead_profile: Record<string, unknown> | null;
}

function formatDeliveryTime(ts: number): string {
  const diffMs = Date.now() - ts;
  const diffHours = Math.floor(diffMs / (1000 * 60 * 60));
  if (diffHours < 1) return "Just now";
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffHours < 48) return "Yesterday";
  const diffDays = Math.floor(diffHours / 24);
  if (diffDays < 7) return `${diffDays}d ago`;
  return new Date(ts).toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function getLeadName(profile: Record<string, unknown> | null): string {
  if (!profile) return "Unknown";
  const first = String(profile.first_name || "").trim();
  const last = String(profile.last_name || "").trim();
  if (first || last) return `${first} ${last}`.trim();
  return "Unknown";
}

const Wallet = () => {
  const { state } = useAgent();
  const { state: contractsState, actions: contractsActions } = useContracts();
  const { toast } = useToast();

  // Trigger contract claiming and refresh contracts after
  const contractClaim = useEnsureContractClaimed(() => {
    console.log("[Wallet] Claim complete, refreshing contracts...");
    contractsActions.refreshContracts();
  });

  const agentId = state.profile?.id || '';
  const agentEmail = state.profile?.email || '';

  // ── Portal loading state ──
  const [portalLoading, setPortalLoading] = useState(false);

  // ── Pause/Resume loading state ──
  const [pauseLoading, setPauseLoading] = useState<string | null>(null);
  const [resumeLoading, setResumeLoading] = useState<string | null>(null);

  // ── Fetch recent delivered leads for Delivery History ──
  const [deliveryEvents, setDeliveryEvents] = useState<DeliveryEvent[]>([]);

  useEffect(() => {
    if (!agentId) return;

    const fetchDeliveryHistory = async () => {
      const { data, error } = await supabase
        .from("leads")
        .select("id, lead_type_name, created_at, last_updated_at, lead_profile")
        .eq("routed_to_agent_id", agentId)
        .eq("status", "ROUTED")
        .order("last_updated_at", { ascending: false })
        .limit(10);

      if (error) {
        console.error("[Wallet] Failed to fetch delivery history:", error);
        return;
      }
      setDeliveryEvents((data as DeliveryEvent[] | null) ?? []);
    };

    fetchDeliveryHistory();
  }, [agentId]);

  // ── Get ALL contracts for this agent ──
  const allAgentContracts = useMemo(() => {
    const claimedIds = contractClaim.activeContractIds;
    return contractsState.contracts.filter(c =>
      c.agentId === agentId ||
      (agentEmail && c.agentEmail === agentEmail) ||
      claimedIds.includes(c.id)
    );
  }, [contractsState.contracts, agentId, agentEmail, contractClaim.activeContractIds]);

  const activeContracts = useMemo(() => {
    return allAgentContracts.filter(c => c.status === 'active' || c.status === 'at_risk');
  }, [allAgentContracts]);

  const pausedContracts = useMemo(() => {
    return allAgentContracts.filter(c => c.status === 'paused');
  }, [allAgentContracts]);

  const cancelledContracts = useMemo(() => {
    return allAgentContracts.filter(c => c.status === 'cancelled');
  }, [allAgentContracts]);

  const hasContracts = allAgentContracts.length > 0;
  const hasActiveContracts = activeContracts.length > 0;

  // ── Determine subscription state for the banner ──
  const subscriptionState: SubscriptionState = useMemo(() => {
    if (!hasContracts) return "no_subscription";

    // Check for paused contracts (payment failure, agent-requested, or admin-requested)
    if (pausedContracts.length > 0 && activeContracts.length === 0) return "paused";

    // Check for cancel_at_period_end on any active subscription contract
    const cancellingContract = allAgentContracts.find(
      c => c.cancelAtPeriodEnd && (c.status === 'active' || c.status === 'at_risk')
    );
    if (cancellingContract) return "cancelling";

    // Check for cancelled (all contracts ended)
    if (cancelledContracts.length > 0 && activeContracts.length === 0 && pausedContracts.length === 0) {
      return "cancelled";
    }

    // Check if this is a subscription or prepaid
    const hasSubscription = allAgentContracts.some(c => c.contractType === 'monthly_target');
    if (!hasSubscription) return "no_subscription";

    return "active";
  }, [hasContracts, pausedContracts, activeContracts, cancelledContracts, allAgentContracts]);

  // ── Determine Stripe customer status ──
  const hasStripeCustomer = useMemo(() => {
    return allAgentContracts.some(c => !!c.stripeCustomerId);
  }, [allAgentContracts]);

  const primaryContractType = useMemo(() => {
    if (allAgentContracts.length === 0) return undefined;
    return allAgentContracts[0].contractType;
  }, [allAgentContracts]);

  // ── Next billing date (cycle_end of earliest ending active subscription) ──
  const nextBillingDate = useMemo(() => {
    const subscriptionContracts = activeContracts.filter(c => c.contractType === 'monthly_target');
    if (subscriptionContracts.length === 0) return undefined;
    const earliest = Math.min(...subscriptionContracts.map(c => c.cycleEnd));
    return new Date(earliest);
  }, [activeContracts]);

  // ── Cancellation date ──
  const cancelsAtDate = useMemo(() => {
    const cancellingContract = allAgentContracts.find(
      c => c.cancelAtPeriodEnd && (c.status === 'active' || c.status === 'at_risk')
    );
    if (!cancellingContract) return undefined;
    return new Date(cancellingContract.cycleEnd);
  }, [allAgentContracts]);

  // ── Open Stripe portal ──
  const openBillingPortal = useCallback(async () => {
    setPortalLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast({
          title: "Authentication required",
          description: "Please sign in again to access billing.",
          variant: "destructive",
        });
        setPortalLoading(false);
        return;
      }

      const { data, error } = await supabase.functions.invoke("create-portal-session", {
        headers: { Authorization: `Bearer ${session.access_token}` },
      });

      if (error || !data?.url) {
        toast({
          title: "Billing portal unavailable",
          description: error?.message || data?.error || "Could not open billing portal.",
          variant: "destructive",
        });
        setPortalLoading(false);
        return;
      }

      window.open(data.url, "_blank", "noopener,noreferrer");
    } catch (err) {
      console.error("[Wallet] Portal error:", err);
      toast({
        title: "Error",
        description: "Could not open billing portal. Please try again.",
        variant: "destructive",
      });
    } finally {
      setPortalLoading(false);
    }
  }, [toast]);

  // ── Pause delivery (agent self-service) ──
  const handlePauseDelivery = useCallback(async (contractId: string) => {
    setPauseLoading(contractId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast({ title: "Authentication required", description: "Please sign in again.", variant: "destructive" });
        return;
      }

      const { data, error } = await supabase.functions.invoke("pause-delivery", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { contractId, reason: "agent_requested" },
      });

      // When Edge Function returns non-2xx, supabase-js puts response in error.context
      let errorBody: Record<string, unknown> | null = null;
      if (error && typeof error === "object" && "context" in error) {
        try {
          errorBody = await (error as unknown as { context: Response }).context.json();
        } catch { /* ignore parse failures */ }
      }

      if (error || !data?.success) {
        const errCode = errorBody?.error ?? data?.error;
        // The RPC echoes the cycle-aware pause cap on `pause_limit_reached`,
        // so use it verbatim instead of hardcoding 14 (wrong for biweekly,
        // weekly, or any non-monthly cycle).
        const rpcMaxSeconds =
          (errorBody?.max_pause_seconds as number | undefined) ??
          (data?.max_pause_seconds as number | undefined);
        const rpcMaxDays = rpcMaxSeconds ? Math.floor(rpcMaxSeconds / 86400) : null;
        const errorMsg = errCode === "pause_limit_reached"
          ? `You've used all ${rpcMaxDays ?? 14} days of pause available this billing cycle.`
          : (typeof errCode === "string" && errCode !== "pause_failed" ? errCode : null)
            || data?.detail || "Could not pause delivery.";
        toast({ title: "Cannot pause delivery", description: errorMsg, variant: "destructive" });
        return;
      }

      const pausedContract = displayContracts.find((c) => c.id === contractId);
      const maxDaysForToast = pausedContract
        ? getMaxPauseDays(getCycleDays(pausedContract.cycleStart, pausedContract.cycleEnd))
        : 14;
      toast({ title: "Delivery paused", description: `Your lead delivery has been paused. You can resume anytime within ${maxDaysForToast} days.` });
      contractsActions.refreshContracts();
    } catch (err) {
      console.error("[Wallet] Pause error:", err);
      toast({ title: "Error", description: "Could not pause delivery. Please try again.", variant: "destructive" });
    } finally {
      setPauseLoading(null);
    }
  }, [toast, contractsActions]);

  // ── Resume delivery (agent self-service) ──
  const handleResumeDelivery = useCallback(async (contractId: string) => {
    setResumeLoading(contractId);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.access_token) {
        toast({ title: "Authentication required", description: "Please sign in again.", variant: "destructive" });
        return;
      }

      const { data, error } = await supabase.functions.invoke("resume-delivery", {
        headers: { Authorization: `Bearer ${session.access_token}` },
        body: { contractId },
      });

      let errorBody: Record<string, unknown> | null = null;
      if (error && typeof error === "object" && "context" in error) {
        try {
          errorBody = await (error as unknown as { context: Response }).context.json();
        } catch { /* ignore parse failures */ }
      }

      if (error || !data?.success) {
        const errDetail = errorBody?.detail ?? errorBody?.error ?? data?.error;
        toast({
          title: "Cannot resume delivery",
          description: (typeof errDetail === "string" ? errDetail : null) || "Could not resume delivery.",
          variant: "destructive",
        });
        return;
      }

      toast({ title: "Delivery resumed", description: "Your lead delivery is back on. Leads will start flowing again shortly." });
      contractsActions.refreshContracts();
    } catch (err) {
      console.error("[Wallet] Resume error:", err);
      toast({ title: "Error", description: "Could not resume delivery. Please try again.", variant: "destructive" });
    } finally {
      setResumeLoading(null);
    }
  }, [toast, contractsActions]);

  // ── Aggregated delivery progress from ACTIVE contracts ──
  const displayContracts = useMemo(() => {
    return activeContracts.length > 0 ? activeContracts : pausedContracts;
  }, [activeContracts, pausedContracts]);
  const totalContracted = displayContracts.reduce((sum, c) => sum + c.totalLeadsCommitted, 0);
  const totalDelivered = displayContracts.reduce((sum, c) => sum + c.leadsDelivered, 0);
  const remaining = Math.max(0, totalContracted - totalDelivered);
  const progressPercent = totalContracted > 0 ? Math.min(100, (totalDelivered / totalContracted) * 100) : 0;

  // ── Contract period ──
  const cycleStart = useMemo(() => {
    if (displayContracts.length === 0) return new Date();
    const earliest = Math.min(...displayContracts.map(c => c.cycleStart));
    return new Date(earliest);
  }, [displayContracts]);

  const cycleEnd = useMemo(() => {
    if (displayContracts.length === 0) return new Date();
    const latest = Math.max(...displayContracts.map(c => c.cycleEnd));
    return new Date(latest);
  }, [displayContracts]);

  // ── Delivery status badge ──
  const getDeliveryStatus = () => {
    if (subscriptionState === "paused") return { label: 'Paused', variant: 'secondary' as const };
    if (subscriptionState === "cancelled") return { label: 'Ended', variant: 'outline' as const };
    if (subscriptionState === "cancelling") return { label: 'Cancelling', variant: 'secondary' as const };
    if (remaining <= 0) return { label: 'Complete', variant: 'default' as const };
    if (progressPercent >= 80) return { label: 'On Track', variant: 'default' as const };
    if (progressPercent >= 50) return { label: 'In Progress', variant: 'secondary' as const };
    return { label: 'Active', variant: 'outline' as const };
  };

  const status = getDeliveryStatus();

  // ── Contract type label ──
  const getContractTypeLabel = () => {
    if (!hasActiveContracts && pausedContracts.length > 0) {
      const reason = pausedContracts[0]?.pausedReason;
      if (reason === "agent_requested") return "You paused delivery. Resume anytime from below.";
      if (reason === "admin_requested") return "Delivery paused by your account manager.";
      return "Payment issue — update your payment method to resume delivery.";
    }
    if (!hasActiveContracts) return 'Your contracted lead delivery volume.';
    if (displayContracts.length > 1) {
      return `${displayContracts.length} active contracts combined.`;
    }
    const primary = displayContracts[0];
    if (!primary) return 'Your contracted lead delivery volume.';
    if (primary.contractType === 'prepaid_package') {
      return 'Prepaid package. Delivered until fulfilled.';
    }
    if (primary.contractType === 'trial') {
      return 'Trial package. Experience our delivery quality.';
    }
    if (primary.contractType === 'monthly_target') {
      const { label } = getPlanLabel('monthly_target', getCycleDays(primary.cycleStart, primary.cycleEnd));
      return `${label} — renews automatically.`;
    }
    return 'Your contracted lead delivery volume.';
  };

  // ── Contracts breakdown items ──
  const contractBreakdownItems: ContractBreakdownItem[] = useMemo(() => {
    return allAgentContracts.map(c => ({
      id: c.id,
      contractType: c.contractType,
      totalLeadsCommitted: c.totalLeadsCommitted,
      leadsDelivered: c.leadsDelivered,
      cycleStart: c.cycleStart,
      cycleEnd: c.cycleEnd,
      status: c.status,
    }));
  }, [allAgentContracts]);

  // ── Loading state ──
  const isLoading = !contractClaim.ready || !state.loaded;

  return (
    <DashboardLayout>
      <div className="space-y-5">
        {/* Page Header */}
        <div>
          <h1 className="font-display text-2xl font-bold text-foreground flex items-center gap-2">
            <WalletIcon className="w-6 h-6" />
            Contract & Billing
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Manage your subscription, track delivery, and access billing
          </p>
        </div>

        {/* Loading State */}
        {isLoading && (
          <div className="rounded-xl border bg-card p-6 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">Loading contract information...</p>
          </div>
        )}

        {/* No Contracts State */}
        {!isLoading && !hasContracts && (
          <div className="rounded-xl border border-accent/30 bg-accent/5 p-6 text-center">
            <FileText className="w-8 h-8 text-accent mx-auto mb-3" />
            <h3 className="font-semibold text-foreground mb-1">No active contract found</h3>
            <p className="text-sm text-muted-foreground">
              Your contract may not be linked to your account yet. Try refreshing the page, or contact your account manager.
            </p>
          </div>
        )}

        {/* ── Subscription Status Banner ── */}
        {!isLoading && hasContracts && (
          <SubscriptionStatusBanner
            state={subscriptionState}
            nextBillingDate={nextBillingDate}
            cancelsAt={cancelsAtDate}
            onManageBilling={hasStripeCustomer ? openBillingPortal : undefined}
            onResume={pausedContracts.length === 1 ? () => handleResumeDelivery(pausedContracts[0].id) : undefined}
            loading={portalLoading || resumeLoading !== null}
            pausedReason={pausedContracts.length > 0 ? pausedContracts[0].pausedReason : undefined}
            maxPauseDays={pausedContracts.length > 0
              ? getMaxPauseDays(getCycleDays(pausedContracts[0].cycleStart, pausedContracts[0].cycleEnd))
              : undefined}
          />
        )}

        {/* ── Primary: Contract & Delivery Overview ── */}
        {hasContracts && displayContracts.length > 0 && (
          <div className="bg-card rounded-2xl border border-border p-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3 mb-5">
              <div>
                <div className="flex items-center gap-2">
                  <h2 className="font-display text-lg font-semibold text-foreground">
                    Delivery Progress
                  </h2>
                  <Badge variant={status.variant}>
                    {status.label}
                  </Badge>
                </div>
                <p className="text-sm text-muted-foreground mt-0.5">
                  {format(cycleStart, 'MMM d')} – {format(cycleEnd, 'MMM d, yyyy')}
                  {displayContracts.length > 1 && (
                    <span className="ml-1 opacity-70">· {displayContracts.length} contracts</span>
                  )}
                </p>
              </div>
            </div>

            {/* Delivery Progress */}
            <div className="space-y-3">
              <div className="flex items-baseline justify-between">
                <span className="text-3xl font-bold text-foreground">{totalDelivered}</span>
                <span className="text-sm text-muted-foreground">
                  of {totalContracted} leads · {remaining} remaining
                </span>
              </div>

              <Progress value={progressPercent} className="h-2.5" />

              <p className="text-xs text-muted-foreground">
                {getContractTypeLabel()}
              </p>
            </div>

            {/* Actions */}
            <div className="mt-5 pt-4 border-t border-border flex flex-wrap gap-2">
              <Link to="/dashboard/delivery-settings">
                <Button variant="outline" size="sm" className="gap-1.5">
                  <TrendingUp className="w-3.5 h-3.5" />
                  Delivery Speed
                  <ArrowRight className="w-3 h-3 opacity-50" />
                </Button>
              </Link>

              {/* Pause buttons for active contracts */}
              {activeContracts.map(c => (
                <Button
                  key={`pause-${c.id}`}
                  variant="outline"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => handlePauseDelivery(c.id)}
                  disabled={pauseLoading === c.id}
                >
                  {pauseLoading === c.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <PauseCircle className="w-3.5 h-3.5" />
                  )}
                  Pause{activeContracts.length > 1 ? ` (${c.agentName || c.id.slice(-6)})` : ""}
                </Button>
              ))}

              {/* Resume buttons for paused contracts */}
              {pausedContracts.map(c => (
                <Button
                  key={`resume-${c.id}`}
                  variant="default"
                  size="sm"
                  className="gap-1.5"
                  onClick={() => handleResumeDelivery(c.id)}
                  disabled={resumeLoading === c.id}
                >
                  {resumeLoading === c.id ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <PlayCircle className="w-3.5 h-3.5" />
                  )}
                  Resume{pausedContracts.length > 1 ? ` (${c.agentName || c.id.slice(-6)})` : ""}
                </Button>
              ))}
            </div>
          </div>
        )}

        {/* ── Contracts Breakdown (multi-contract agents) ── */}
        {hasContracts && <ContractsBreakdownCard contracts={contractBreakdownItems} />}

        {/* ── Contract Amendment History ── */}
        {hasContracts && (
          <AgentAmendmentHistory contractIds={allAgentContracts.map(c => c.id)} />
        )}

        {/* ── Billing Management ── */}
        <BillingManagementCard
          hasStripeCustomer={hasStripeCustomer}
          contractType={primaryContractType}
        />

        {/* ── Two-Column Layout: Payment Activity + Delivery History ── */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          {/* Payment Activity Feed */}
          {hasContracts && (
            <PaymentActivityFeed agentId={agentId} agentEmail={agentEmail} />
          )}

          {/* Delivery History */}
          <div className="bg-card rounded-2xl border border-border overflow-hidden">
            <div className="px-5 py-4 border-b border-border">
              <h3 className="font-display text-base font-semibold text-foreground">Recent Deliveries</h3>
              <p className="text-xs text-muted-foreground mt-0.5">
                {deliveryEvents.length > 0
                  ? `Last ${deliveryEvents.length} lead${deliveryEvents.length > 1 ? "s" : ""} delivered`
                  : "Delivery events will appear here"}
              </p>
            </div>

            {deliveryEvents.length === 0 ? (
              <div className="px-5 py-8 text-center text-muted-foreground">
                <p className="text-sm">No delivery events yet</p>
              </div>
            ) : (
              <div className="divide-y divide-border">
                {deliveryEvents.map((event) => {
                  const name = getLeadName(event.lead_profile);
                  return (
                    <Link
                      key={event.id}
                      to={`/dashboard/leads/${event.id}`}
                      className="flex items-center gap-3 px-5 py-3 hover:bg-muted/30 transition-colors"
                    >
                      <div className="w-7 h-7 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <UserCheck className="w-3.5 h-3.5 text-primary" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-foreground truncate">{name}</p>
                        <p className="text-xs text-muted-foreground">{event.lead_type_name}</p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">
                        {formatDeliveryTime(event.last_updated_at)}
                      </span>
                    </Link>
                  );
                })}
              </div>
            )}
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="rounded-xl p-4">
          <p className="text-xs text-muted-foreground text-center leading-relaxed">
            Your contract defines your lead entitlement. Delivery is managed automatically.
            {hasStripeCustomer && (
              <>
                {" "}
                <button
                  onClick={openBillingPortal}
                  disabled={portalLoading}
                  className="text-primary hover:underline"
                >
                  Manage billing
                </button>
                {" or "}
              </>
            )}
            <Link to="/dashboard/messages" className="text-primary hover:underline">
              contact support
            </Link>
            {" "}for questions.
          </p>
        </div>
      </div>
    </DashboardLayout>
  );
};

export default Wallet;
